import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Modal } from './Modal'
import { supabase } from '@/lib/supabase'
import type { ReferenciaBancariaAsociado } from '@/lib/typesNeoCore'

/**
 * Solicitar retiro por transferencia DESDE el Core → cae en la bandeja de
 * Solicitudes de Retiros (estado 'solicitado', sin debitar todavía).
 *
 * Este es el camino correcto para retiros de asociado: NO usar "Nueva operación
 * → Transferencia bancaria" (esa registra el movimiento de caja directo y salta
 * la cola). Acá solo se encola; el débito + contabilidad ocurren cuando se marca
 * acreditado al final del flujo de la bandeja (acreditar_retiro_admin), después
 * de generar y subir el lote ACH al banco.
 *
 * Llama al RPC solicitar_retiro_admin(p_asociado_id, p_monto, p_cuenta_bancaria_id, p_nota).
 * El RPC valida saldo disponible (ahorro − retiros en proceso) y que la cuenta
 * esté verificada, así que es la fuente de verdad; acá el saldo es solo guía.
 */

type Props = {
  open: boolean
  onClose: () => void
  asociadoId: string
  asociadoNombre: string
  referencia: ReferenciaBancariaAsociado | null
  saldoAhorro: number
  onSuccess: () => void
}

const money = (n: number) =>
  'Q ' + n.toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export function SolicitarRetiroModal({
  open, onClose, asociadoId, asociadoNombre, referencia, saldoAhorro, onSuccess,
}: Props) {
  const [monto, setMonto] = useState('')
  const [nota, setNota] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ok, setOk] = useState(false)
  // El asociado tiene un crédito activo → el retiro solo procede como gestión
  // explícita del operador (queda auditado como retiro.gestion_con_credito).
  const [gestionCredito, setGestionCredito] = useState(false)

  useEffect(() => {
    if (!open) return
    setError(null); setOk(false); setMonto(''); setNota(''); setGestionCredito(false)
  }, [open])

  const montoNum = useMemo(() => Number(monto) || 0, [monto])
  const excede = montoNum > saldoAhorro

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!referencia) { setError('El asociado no tiene cuenta bancaria verificada.'); return }
    if (montoNum <= 0) { setError('Ingresá un monto válido.'); return }
    if (excede) { setError(`El monto supera el saldo disponible (${money(saldoAhorro)}).`); return }
    if (gestionCredito && !nota.trim()) {
      setError('Como es una gestión con crédito activo, dejá una nota explicando el motivo.'); return
    }

    setSubmitting(true)
    const { data, error: rpcErr } = await supabase.rpc('solicitar_retiro_admin', {
      p_asociado_id: asociadoId,
      p_monto: montoNum,
      p_cuenta_bancaria_id: referencia.id,
      p_nota: nota.trim() || null,
      p_forzar_con_credito: gestionCredito,
    })
    setSubmitting(false)

    if (rpcErr) {
      // El asociado tiene un crédito activo: el candado lo frena. Habilitamos el
      // camino de gestión para que el operador lo procese conscientemente.
      if (rpcErr.message.includes('[CREDITO_ACTIVO]')) {
        setGestionCredito(true)
        setError('⚠️ Este asociado tiene un crédito activo. Validá el caso; si corresponde, dejá una nota y volvé a enviar para procesarlo como gestión.')
        return
      }
      setError(rpcErr.message); return
    }
    if (data && (data as { ok?: boolean }).ok === false) {
      setError('No se pudo crear la solicitud.'); return
    }

    setOk(true)
    onSuccess()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Solicitar retiro por transferencia"
      subtitle={asociadoNombre}
      size="md"
      blocking={submitting}
      footer={
        ok ? (
          <button type="button" className="btn btn-primary" onClick={onClose}>Listo</button>
        ) : (
          <>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
              Cancelar
            </button>
            <button
              type="submit" form="solicitar-retiro-form" className="btn btn-primary"
              disabled={submitting || !referencia}
            >
              {submitting ? 'Enviando…' : gestionCredito ? 'Procesar como gestión' : 'Enviar a la bandeja'}
            </button>
          </>
        )
      }
    >
      {ok ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div
            style={{
              padding: '12px 14px', background: 'var(--ok-soft, #ecfdf5)',
              border: '1px solid var(--ok-line, #a7f3d0)', borderRadius: 10,
              fontSize: 13, color: 'var(--ok-ink, #065f46)', lineHeight: 1.55,
            }}
          >
            Retiro de <strong>{money(montoNum)}</strong> enviado a <strong>Solicitudes de Retiros</strong>.
            Desde ahí generás el lote ACH, lo subís al banco y marcás acreditado — ahí se
            debita del ahorro y se contabiliza.
          </div>
        </div>
      ) : (
        <form id="solicitar-retiro-form" onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error && <div className="error-banner" style={{ margin: 0 }}>{error}</div>}

          {!referencia ? (
            <div
              style={{
                padding: '12px 14px', background: '#fff7ed',
                border: '1px solid #fed7aa', borderRadius: 10,
                fontSize: 12, color: '#9a3412', lineHeight: 1.55,
              }}
            >
              Este asociado no tiene una cuenta bancaria verificada. Capturala o verificala
              desde el tab <strong>Cumplimiento → Cuenta bancaria</strong> antes de solicitar el retiro.
            </div>
          ) : (
            <div
              style={{
                padding: '10px 12px', background: 'var(--soft-gray, #f8fafc)',
                border: '1px solid var(--hairline)', borderRadius: 10,
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                Transferir a
              </div>
              <div style={{ marginTop: 4, fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>
                {referencia.banco}
              </div>
              <div style={{ marginTop: 2, fontFamily: 'var(--font-mono)', color: 'var(--ink-soft)' }}>
                {referencia.numero_cuenta} · {referencia.tipo.toLowerCase()}
              </div>
              <div style={{ marginTop: 2, fontSize: 12 }}>
                A nombre de: <strong>{referencia.titular_nombre}</strong>
              </div>
            </div>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="form-label" style={{ fontSize: 11 }}>Monto a retirar</span>
            <input
              inputMode="decimal"
              value={monto}
              onChange={(e) => setMonto(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="0.00"
              disabled={!referencia}
            />
            <span style={{ fontSize: 11, color: excede ? 'var(--danger, #b91c1c)' : 'var(--muted-foreground)', marginTop: 4 }}>
              Saldo disponible en ahorro: {money(saldoAhorro)}
            </span>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="form-label" style={{ fontSize: 11 }}>Nota (opcional)</span>
            <input
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Ej. Retiro solicitado por WhatsApp"
              disabled={!referencia}
            />
          </label>
        </form>
      )}
    </Modal>
  )
}
